import { inject, Injectable, signal } from '@angular/core'
import { Auth0Client, createAuth0Client } from '@auth0/auth0-spa-js'
import { environment } from '@environment'
import { adminEnvironment } from '@environment-admin'
import { ToasterService } from './toaster.service'
import { User } from '../models/user.model'

export interface PaginationParams {
  page?: number
  limit?: number
  search?: string
}

export interface SharePublishersRequest {
  publisherIds: string[]
  userIds: string[]
}

@Injectable({ providedIn: 'root' })
export class AdminAuthService {
  private client: Auth0Client | null = null
  private readonly apiBaseUrl = environment.adminApiUrl
  private toasterService = inject(ToasterService)

  isAuthenticated = signal(false)
  allUsers = signal<User[]>([])

  async loadAllUsers(): Promise<void> {
    try {
      const response = (await this.listUsers({ page: 1, limit: 1000 })) as { users: User[]; total: number }
      this.allUsers.set(response.users ?? [])
    } catch (error) {
      console.error('Error loading all users:', error)
    }
  }

  constructor() {}

  private async getClient(): Promise<Auth0Client> {
    if (!this.client) {
      this.client = await createAuth0Client({
        domain: adminEnvironment.auth0.domain,
        clientId: adminEnvironment.auth0.clientId,
        authorizationParams: {
          audience: adminEnvironment.auth0.audience,
        },
        cacheLocation: 'localstorage',
        useRefreshTokens: true,
      })
    }
    return this.client
  }

  async login(): Promise<void> {
    const client = await this.getClient()
    await client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/vm-mh-admin-app`,
      },
    })
  }

  async handleRedirectCallback(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search)
    if (!params.has('code') || !params.has('state')) return false

    const client = await this.getClient()
    await client.handleRedirectCallback()
    this.isAuthenticated.set(true)
    return true
  }

  async checkAuth(): Promise<boolean> {
    const client = await this.getClient()
    const authenticated = await client.isAuthenticated()
    this.isAuthenticated.set(authenticated)
    return authenticated
  }

  async getToken(): Promise<string | undefined> {
    const client = await this.getClient()
    return client.getTokenSilently()
  }

  async logout(): Promise<void> {
    const client = await this.getClient()
    this.isAuthenticated.set(false)
    await client.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/vm-mh-admin-app`,
      },
    })
  }

  private async authFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = await this.getToken()
    const res = await fetch(`${this.apiBaseUrl}${endpoint}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })
    if (!res.ok) {
      throw new Error(`Admin API error: ${res.status} ${res.statusText}`)
    }
    return res.json()
  }

  async healthCheck(): Promise<unknown> {
    const res = await fetch(`${this.apiBaseUrl}/health-check`)
    return res.json()
  }

  async authCheck(): Promise<unknown> {
    return this.authFetch('/auth-check')
  }

  async listUsers(params: PaginationParams = {}): Promise<unknown> {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.limit) query.set('limit', String(params.limit))
    if (params.search) query.set('search', params.search)
    const qs = query.toString()
    return this.authFetch(`/users${qs ? `?${qs}` : ''}`)
  }

  async listPublishers(params: PaginationParams = {}): Promise<unknown> {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.limit) query.set('limit', String(params.limit))
    if (params.search) query.set('search', params.search)
    const qs = query.toString()
    return this.authFetch(`/publishers${qs ? `?${qs}` : ''}`)
  }

  async sharePublishers(data: SharePublishersRequest): Promise<void> {
    try {
      const response: any = await this.authFetch('/publishers/share', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      this.toasterService.success(response?.message ?? 'Sources shared successfully.')
    } catch (error: any) {
      this.toasterService.error(error?.message ?? 'An error occurred during publisher sharing.')
      throw error
    }
  }

  async unsharePublishers(data: SharePublishersRequest): Promise<void> {
    try {
      const response: any = await this.authFetch('/publishers/unshare', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      this.toasterService.success(response?.message ?? 'Sources unshared successfully.')
    } catch (error: any) {
      this.toasterService.error(error?.message ?? 'An error occurred during publisher unsharing.')
      throw error
    }
  }
}
